import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import LineManagement from './pages/Line';
import LineConnect from './pages/Line/Connect/index';
import LineEdit from './pages/Line/Edit';
import { RequireAuth } from './components/RequireAuth';
import { RequireAdmin } from './components/RequireAdmin';
import { ApiErrorWatcher } from './components/ApiErrorWatcher';
import { OrganizationScope } from './components/OrganizationScope';
import { TermsGateListener } from './components/TermsGateListener';
import { commonStrings } from './constants/strings';
import { ORGANIZATION_SCOPE_ROOT, orgRoutePath, routes } from './constants/routes';
import Login from './pages/Login';
import EntryRedirect from './pages/Entry';
import SelectOrganization from './pages/SelectOrganization';
import TermsConsent from './pages/TermsConsent';
import TermsManagement from './pages/TermsManagement';
import TermsManagementEditor from './pages/TermsManagement/Editor';
import Chat from './pages/Chat';
import SendMessageForAll from './pages/SendMessageForAll';
import Users from './pages/Users';
import UserEdit from './pages/Users/Edit';
import AdCodes from './pages/AdCodes';
import AdCodeCreate from './pages/AdCodes/Create';
import AdCodeDetail from './pages/AdCodes/Detail';
import AdCodeEdit from './pages/AdCodes/Edit';
import TagManagement from './pages/TagManagement';
import TagManagementCreate from './pages/TagManagement/Create';
import TagManagementEdit from './pages/TagManagement/Edit';
import AccountManagement from './pages/AccountManagement';
import AccountManagementCreate from './pages/AccountManagement/Create';
import AccountManagementEdit from './pages/AccountManagement/Edit';
import BanStatus from './pages/BanStatus';
import OrganizationManagement from './pages/OrganizationManagement';
import OrganizationManagementCreate from './pages/OrganizationManagement/Create';
import OrganizationManagementEdit from './pages/OrganizationManagement/Edit';
import DraftsList from './pages/SendMessageForAll/Drafts';
import ReservationsList from './pages/SendMessageForAll/Reservations';
import SendHistory from './pages/SendHistory';
import { RequireChatManagement } from './components/RequireChatManagement';
import { RequireInternalPlan } from './components/RequireInternalPlan';
import { RequireOrganizationFeature } from './components/RequireOrganizationFeature';
import { EOrganizationFeature } from './types/enum';
import RichMenuPage from './pages/RichMenu/Create';
import MessageSettings from './pages/MessageSettings';
import OaCreation from './pages/OaCreation';
import InquiryChat from './pages/InquiryChat';
import AiKnowledge from './pages/AiKnowledge';
import AutoReplyMessagePage from './pages/AutoReplyMessage';
import RichMessagePage from './pages/RichMessage';
import CardTypeMessagePage from './pages/CardTypeMessage';
import Scenarios from './pages/Scenarios';
import ScenarioEdit from './pages/Scenarios/Edit';
import ScenarioPreview from './pages/Scenarios/Preview';
import ScenarioStats from './pages/Scenarios/Stats';
import SurveyManagement from './pages/Survey';
import SurveyEdit from './pages/Survey/Edit';
import SurveyAggregation from './pages/Survey/Aggregation';

const FriendAnalytics = lazy(() => import('./pages/FriendAnalytics'));

function App() {
  return (
    <>
      <ApiErrorWatcher />
      <Routes>
        <Route path={routes.LOGIN} element={<Login />} />
        {/* 利用規約の同意画面は AppLayout・RequireAuth の外（PubNub・通知登録・業務APIを開始しない） */}
        <Route path={routes.TERMS_CONSENT} element={<TermsConsent />} />
        <Route element={<RequireAuth />}>
          <Route path={routes.HOME} element={<EntryRedirect />} />
          <Route
            path={routes.SELECT_ORGANIZATION}
            element={<SelectOrganization />}
          />
          <Route element={<RequireAdmin />}>
            <Route path={routes.BAN_STATUS} element={<BanStatus />} />
            <Route
              path={routes.ORGANIZATION_MANAGEMENT}
              element={<OrganizationManagement />}
            />
            <Route
              path={routes.ACCOUNT_MANAGEMENT}
              element={<AccountManagement />}
            />
            <Route
              path={routes.ORGANIZATION_MANAGEMENT_CREATE}
              element={<OrganizationManagementCreate />}
            />
            <Route
              path={routes.ORGANIZATION_MANAGEMENT_EDIT}
              element={<OrganizationManagementEdit />}
            />
            <Route
              path={routes.ACCOUNT_MANAGEMENT_CREATE}
              element={<AccountManagementCreate />}
            />
            <Route
              path={routes.ACCOUNT_MANAGEMENT_EDIT}
              element={<AccountManagementEdit />}
            />
            <Route
              path={routes.OA_CREATION}
              element={
                <RequireInternalPlan>
                  <OaCreation />
                </RequireInternalPlan>
              }
            />
            <Route path={routes.AI_KNOWLEDGE} element={<AiKnowledge />} />
            <Route
              path={routes.TERMS_MANAGEMENT}
              element={<TermsManagement />}
            />
            <Route
              path={routes.TERMS_MANAGEMENT_CREATE}
              element={<TermsManagementEditor />}
            />
            <Route
              path={routes.TERMS_MANAGEMENT_DETAIL}
              element={<TermsManagementEditor />}
            />
          </Route>

          <Route path={ORGANIZATION_SCOPE_ROOT} element={<OrganizationScope />}>
            <Route index element={<Navigate to="line" replace />} />
            <Route
              path={orgRoutePath(routes.LINE)}
              element={<LineManagement />}
            />
            <Route
              path={orgRoutePath(routes.LINE_CONNECT)}
              element={<LineConnect />}
            />
            <Route path={orgRoutePath(routes.LINE_EDIT)} element={<LineEdit />} />
            <Route
              path={orgRoutePath(routes.RICH_MENU)}
              element={<RichMenuPage />}
            />
            <Route path={orgRoutePath(routes.USERS)} element={<Users />} />
            <Route
              path={orgRoutePath(routes.USERS_EDIT)}
              element={<UserEdit />}
            />
            <Route path={orgRoutePath(routes.AD_CODES)} element={<AdCodes />} />
            <Route
              path={orgRoutePath(routes.AD_CODES_CREATE)}
              element={<AdCodeCreate />}
            />
            <Route
              path={orgRoutePath(routes.AD_CODES_EDIT)}
              element={<AdCodeEdit />}
            />
            <Route
              path={orgRoutePath(routes.AD_CODES_DETAIL)}
              element={<AdCodeDetail />}
            />
            <Route
              path={orgRoutePath(routes.TAG_MANAGEMENT)}
              element={
                <RequireOrganizationFeature feature={EOrganizationFeature.TAG_MANAGEMENT}>
                  <TagManagement />
                </RequireOrganizationFeature>
              }
            />
            <Route
              path={orgRoutePath(routes.TAG_MANAGEMENT_CREATE)}
              element={
                <RequireOrganizationFeature feature={EOrganizationFeature.TAG_MANAGEMENT}>
                  <TagManagementCreate />
                </RequireOrganizationFeature>
              }
            />
            <Route
              path={orgRoutePath(routes.TAG_MANAGEMENT_EDIT)}
              element={
                <RequireOrganizationFeature feature={EOrganizationFeature.TAG_MANAGEMENT}>
                  <TagManagementEdit />
                </RequireOrganizationFeature>
              }
            />
            <Route
              path={orgRoutePath(routes.SURVEY_MANAGEMENT)}
              element={
                <RequireOrganizationFeature feature={EOrganizationFeature.SURVEY}>
                  <SurveyManagement />
                </RequireOrganizationFeature>
              }
            />
            <Route
              path={orgRoutePath(routes.SURVEY_MANAGEMENT_CREATE)}
              element={
                <RequireOrganizationFeature feature={EOrganizationFeature.SURVEY}>
                  <SurveyEdit />
                </RequireOrganizationFeature>
              }
            />
            <Route
              path={orgRoutePath(routes.SURVEY_MANAGEMENT_EDIT)}
              element={
                <RequireOrganizationFeature feature={EOrganizationFeature.SURVEY}>
                  <SurveyEdit />
                </RequireOrganizationFeature>
              }
            />
            <Route
              path={orgRoutePath(routes.SURVEY_MANAGEMENT_AGGREGATION)}
              element={
                <RequireOrganizationFeature feature={EOrganizationFeature.SURVEY}>
                  <SurveyAggregation />
                </RequireOrganizationFeature>
              }
            />
            <Route
              path={orgRoutePath(routes.CHAT)}
              element={
                <RequireChatManagement>
                  <Chat />
                </RequireChatManagement>
              }
            />
            <Route
              path={orgRoutePath(routes.INQUIRY_CHAT)}
              element={<InquiryChat />}
            />
            <Route
              path={orgRoutePath(routes.SEND_MESSAGE_FOR_ALL)}
              element={<SendMessageForAll />}
            />
            <Route path={orgRoutePath(routes.DRAFTS)} element={<DraftsList />} />
            <Route
              path={orgRoutePath(routes.RESERVATIONS)}
              element={<ReservationsList />}
            />
            <Route
              path={orgRoutePath(routes.SCENARIOS)}
              element={
                <RequireOrganizationFeature feature={EOrganizationFeature.STEP_DELIVERY}>
                  <Scenarios />
                </RequireOrganizationFeature>
              }
            />
            <Route
              path={orgRoutePath(routes.SCENARIOS_NEW)}
              element={
                <RequireOrganizationFeature feature={EOrganizationFeature.STEP_DELIVERY}>
                  <Scenarios />
                </RequireOrganizationFeature>
              }
            />
            <Route
              path={orgRoutePath(routes.SCENARIO_EDIT)}
              element={
                <RequireOrganizationFeature feature={EOrganizationFeature.STEP_DELIVERY}>
                  <ScenarioEdit />
                </RequireOrganizationFeature>
              }
            />
            <Route
              path={orgRoutePath(routes.SCENARIO_STATS)}
              element={
                <RequireOrganizationFeature feature={EOrganizationFeature.STEP_DELIVERY}>
                  <ScenarioStats />
                </RequireOrganizationFeature>
              }
            />
            <Route
              path={orgRoutePath(routes.SCENARIO_PREVIEW)}
              element={
                <RequireOrganizationFeature feature={EOrganizationFeature.STEP_DELIVERY}>
                  <ScenarioPreview />
                </RequireOrganizationFeature>
              }
            />
            <Route
              path={orgRoutePath(routes.SUMMARY_MANAGEMENT)}
              element={<SendHistory />}
            />
            <Route
              path={orgRoutePath(routes.FRIEND_ANALYTICS)}
              element={
                <Suspense
                  fallback={
                    <p role="status" className="p-6 text-sm text-muted">
                      {commonStrings.loading}
                    </p>
                  }
                >
                  <FriendAnalytics />
                </Suspense>
              }
            />
            <Route
              path={orgRoutePath(routes.MESSAGE_SETTINGS)}
              element={<MessageSettings />}
            />
            <Route
              path={orgRoutePath(routes.AUTO_REPLY_MESSAGE)}
              element={<AutoReplyMessagePage />}
            />
            <Route
              path={orgRoutePath(routes.RICH_MESSAGE)}
              element={<RichMessagePage />}
            />
            <Route
              path={orgRoutePath(routes.CARD_TYPE_MESSAGE)}
              element={<CardTypeMessagePage />}
            />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to={routes.HOME} replace />} />
      </Routes>
      <TermsGateListener />
      <ToastContainer
        position="top-right"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        draggableDirection="y"
        draggablePercent={40}
        pauseOnHover={false}
        theme="light"
      />
    </>
  );
}

export default App;
